import { useContext, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { GridContext } from 'contexts/GridContext';
import { toastWarning } from 'utils/toast';
import { DropsContext } from 'contexts/DropsContext';
import { EntregasContext } from 'contexts/EntregasContext';
import { useModal } from 'components/Modals';
import ResumoModal from 'components/Modals/ResumoModal';
import TransmissãoModal from 'views/Notas/Modals/TransmissãoModal';
import Container from 'components/Container';
import Header from 'components/Header';
import Card from 'components/Card';
import DataView, { useDataViewFilters } from 'components/DataView';
import columnOverrides from './columnOverrides';
import getEntregasFilterFields from './filterFields';

const Entregas = () => {
  const rotina = 'Entregas';
  const titulo = 'Entregas de Vendas Realizadas';
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal, closeModal } = useModal();
  const { drops } = useContext(DropsContext);
  const { exportPdf, exportXls } = useContext(GridContext);
  const { entregas, getEntregas, getLoading } = useContext(EntregasContext);

  const filterFields = useMemo(() => getEntregasFilterFields(drops), [drops]);

  const { applyFilter, filterForm } = useDataViewFilters({
    fields: filterFields,
    searchParams,
    setSearchParams,
    onApply: (props) => loadGrid(props),
  });
  const { getValues } = filterForm;

  const loadGrid = (props = {}) => {
    const filter = props?.filter || getValues();

    closeModal();

    getEntregas({ ...props, rotina, filter });
  };

  const toolbarActions = [
    {
      name: 'Exportar PDF',
      icon: 'file_download',
      action: () =>
        exportPdf({ rotina, filter: getValues(), order: entregas?.order }),
    },
    {
      name: 'Exportar XLS',
      icon: 'file_download',
      action: () =>
        exportXls({ rotina, filter: getValues(), order: entregas?.order }),
    },
    {
      name: 'Resumo',
      icon: 'summarize',
      action: () =>
        openModal(
          <ResumoModal
            colunas={entregas?.colunas}
            onSubmit={(group) =>
              navigate('/app/Resumo', {
                state: { rotina, group, filter: getValues() },
              })
            }
          />
        ),
    },
    {
      name: 'Transmitir / Registrar Notas',
      icon: 'send',
      action: (selected) => {
        const notas = selected?.filter((f) =>
          Boolean(f?.status !== 'CONCLUIDO')
        );
        if (!Boolean(notas?.length)) {
          return toastWarning('Selecione apenas documentos não concluídos');
        } else {
          openModal(<TransmissãoModal selected={notas} callback={loadGrid} />);
        }
      },
    },
  ];

  const rowActions = [
    {
      name: 'Detalhes',
      icon: 'visibility',
      action: ({ row }) => navigate(`/app/Documento/${row?.id}`),
    },
    {
      name: 'Realizar Entrega',
      icon: 'local_shipping',
      action: ({ row }) => {
        const natureza = row?.natureza === 111 ? 112 : 31;
        navigate(
          `/app/Remessas/Gerar/${row?.especie}/${natureza}/[]/${row?.id}`
        );
      },
      menu: true,
    },
  ];

  return (
    <Container fillHeight>
      <Header rotina={rotina} titulo={titulo} colunas={entregas?.colunas} />
      <Card fillHeight>
        <DataView
          fillHeight
          grid={entregas}
          filterFields={filterFields}
          filterForm={filterForm}
          onFilter={applyFilter}
          columnOverrides={columnOverrides}
          toolbarActions={toolbarActions}
          rowActions={rowActions}
          checkboxSelection
          loading={getLoading}
          loadGrid={loadGrid}
        />
      </Card>
    </Container>
  );
};

export default Entregas;
